import { parseToHsl, rgba, toColorString } from 'polished';
import { createSelector as select } from 'reselect';
import { applyHSVAdjustment } from '../style/applyHSVAdjustment';
import { getReportThemeColorDerivation, isDarkReportTheme } from './reportThemes';
/**
 * Scales the lightness of the given color
 *
 * The ratio must be between -1 and 1.
 * If negative, the lightness is scaled towards 0. If positive, the lightness is scaled toward 1.
 * Equivalent to color.scale($color, $lightness: $ratio) in Sass.
 */
export function scaleLightness(ratio, color) {
  if (ratio < -1 || ratio > 1) {
    throw new Error('Ratio must be between -1 and 1');
  }
  const hslColor = parseToHsl(color);
  const lightness = hslColor.lightness;
  if (ratio < 0) {
    // darken
    hslColor.lightness = lightness + ratio * lightness;
  } else {
    // lighten
    hslColor.lightness = lightness + ratio * (1 - lightness);
  }
  return toColorString(hslColor);
}
function deriveGraphAltColor(color, theme) {
  switch (theme) {
    case 'high-contrast':
      return applyHSVAdjustment(color, 0, 0, 0.12);
    case 'dark':
      return applyHSVAdjustment(color, 0, 0, 0.32);
    default:
      return applyHSVAdjustment(color, 0, 0, -0.22);
  }
}
export const colorNeutral = theme => theme.colors.colorNeutral;
export const colorStatusNeutral = theme => theme.colors.colorStatusNeutral;
export const colorPrimary = theme => theme.colors.colorPrimary;
export const colorSecondary = theme => theme.colors.colorSecondary;
export const colorAlert = theme => theme.colors.colorAlert;
export const colorWarning = theme => theme.colors.colorWarning;
export const colorSuccess = theme => theme.colors.colorSuccess;
export const colorInfo = theme => theme.colors.colorInfo;
export const colorUiLight = theme => theme.colors.colorUiLight;
export const colorUiMedium = theme => theme.colors.colorUiMedium;
export const colorUiDark = theme => theme.colors.colorUiDark;
export const colorUiBorder = theme => theme.colors.colorUiBorder;
export const colorUiPop = theme => theme.colors.colorUiPop;
export const colorTextDefault = theme => theme.colors.colorTextDefault;
export const colorTextLabel = theme => theme.colors.colorTextLabel;
export const colorTextInverse = theme => theme.colors.colorTextInverse;
export const colorTextPrimary = theme => theme.colors.colorTextPrimary;
export const colorTextSecondary = theme => theme.colors.colorTextSecondary;
export const colorTextPop = theme => theme.colors.colorTextPop;
export const colorGraph1 = theme => theme.colors.colorGraph1;
export const colorGraph2 = theme => theme.colors.colorGraph2;
export const colorGraph3 = theme => theme.colors.colorGraph3;
export const colorGraph4 = theme => theme.colors.colorGraph4;
export const colorGraph5 = theme => theme.colors.colorGraph5;
export const colorGraph6 = theme => theme.colors.colorGraph6;
export const colorGraph7 = theme => theme.colors.colorGraph7;
export const colorGraph8 = theme => theme.colors.colorGraph8;
export const colorGraphAlt1 = theme => deriveGraphAltColor(theme.colors.colorGraph1, theme.baseThemeName);
export const colorGraphAlt2 = theme => deriveGraphAltColor(theme.colors.colorGraph2, theme.baseThemeName);
export const colorGraphAlt3 = theme => deriveGraphAltColor(theme.colors.colorGraph3, theme.baseThemeName);
export const colorGraphAlt4 = theme => deriveGraphAltColor(theme.colors.colorGraph4, theme.baseThemeName);
export const colorGraphAlt5 = theme => deriveGraphAltColor(theme.colors.colorGraph5, theme.baseThemeName);
export const colorGraphAlt6 = theme => deriveGraphAltColor(theme.colors.colorGraph6, theme.baseThemeName);
export const colorGraphAlt7 = theme => deriveGraphAltColor(theme.colors.colorGraph7, theme.baseThemeName);
export const colorGraphAlt8 = theme => deriveGraphAltColor(theme.colors.colorGraph8, theme.baseThemeName);
export const colorKPI1 = theme => theme.colors.colorKPI1;
export const colorKPI3 = theme => theme.colors.colorKPI3;
export const colorKPI5 = theme => theme.colors.colorKPI5;
export const neutral = theme => theme.colors.neutral;
export const primary = theme => theme.colors.primary;
export const secondary = theme => theme.colors.secondary;
export const alert = theme => theme.colors.alert;
export const warning = theme => theme.colors.warning;
export const success = theme => theme.colors.success;
export const informational = theme => theme.colors.information;
const black = () => '#000';
const white = select(isDarkReportTheme, neutral, (isDarkReportTheme, neutral) => {
  return isDarkReportTheme ? neutral : '#fff';
});
export const white80 = select(white, white => rgba(white, 0.8));
export const white60 = select(white, white => rgba(white, 0.6));
export const white30 = select(white, white => rgba(white, 0.3));
export const white20 = select(white, white => rgba(white, 0.2));
export const white15 = select(white, white => rgba(white, 0.15));
export const white10 = select(white, white => rgba(white, 0.1));
export const black60 = select(black, black => rgba(black, 0.6));

// Shift factors should match openui-framework-themes
export const primaryLightenShiftFactor = 1.6949;
export const neutralLightenShiftFactor = 2.1277;
export const secondaryLightenShiftFactor = 1.4493;
export const alertLightenShiftFactor = 1.7544;
export const warningLightenShiftFactor = 1.4085;
export const successLightenShiftFactor = 1.3699;
export const informationLightenShiftFactor = 1.5873;
export const primaryDarkenShiftFactor = 2.5;
export const neutralDarkenShiftFactor = 1.9231;
export const n = neutral;
export const n0 = n;
export const n5 = select(n, n => scaleLightness(0.45 * neutralLightenShiftFactor, n));
export const n4 = select(n, n => scaleLightness(0.43 * neutralLightenShiftFactor, n));
export const n3 = select(n, n => scaleLightness(0.4 * neutralLightenShiftFactor, n));
export const n2 = select(n, n => scaleLightness(0.3 * neutralLightenShiftFactor, n));
export const n1 = select(n, n => scaleLightness(0.2 * neutralLightenShiftFactor, n));
export const n_1 = select(n, n => scaleLightness(-0.08 * neutralDarkenShiftFactor, n));
export const n_2 = select(n, n => scaleLightness(-0.25 * neutralDarkenShiftFactor, n));
export const n_3 = select(n, n => scaleLightness(-0.33 * neutralDarkenShiftFactor, n));
export const n_4 = select(n, n => scaleLightness(-0.4 * neutralDarkenShiftFactor, n));
export const n_5 = select(n, n => scaleLightness(-0.5 * neutralDarkenShiftFactor, n));
export const p = primary;
export const p0 = p;
export const p5 = select(p, p => scaleLightness(0.57 * primaryLightenShiftFactor, p));
export const p4 = select(p, p => scaleLightness(0.55 * primaryLightenShiftFactor, p));
export const p3 = select(p, p => scaleLightness(0.35 * primaryLightenShiftFactor, p));
export const p2 = select(p, p => scaleLightness(0.22 * primaryLightenShiftFactor, p));
export const p1 = select(p, p => scaleLightness(0.11 * primaryLightenShiftFactor, p));

// Primary colors became darker for the 2020 report themes
export const p_1 = select(p, getReportThemeColorDerivation, (p, derivation) => {
  let scaleFactor;
  switch (derivation) {
    case '2019':
      scaleFactor = -0.14;
      break;
    case 'latest':
      scaleFactor = -0.25;
      break;
  }
  return scaleLightness(scaleFactor * primaryDarkenShiftFactor, p);
});
export const p_2 = select(p, getReportThemeColorDerivation, (p, derivation) => {
  let scaleFactor;
  switch (derivation) {
    case '2019':
      scaleFactor = -0.23;
      break;
    case 'latest':
      scaleFactor = -0.28;
      break;
  }
  return scaleLightness(scaleFactor * primaryDarkenShiftFactor, p);
});
export const p_3 = select(p, getReportThemeColorDerivation, (p, derivation) => {
  let scaleFactor;
  switch (derivation) {
    case '2019':
      scaleFactor = -0.3;
      break;
    case 'latest':
      scaleFactor = -0.32;
      break;
  }
  return scaleLightness(scaleFactor * primaryDarkenShiftFactor, p);
});
export const e = secondary;
export const e0 = e;
export const e1 = select(e, e => scaleLightness(0.13 * secondaryLightenShiftFactor, e));
export const e2 = select(e, e => scaleLightness(0.59 * secondaryLightenShiftFactor, e));
export const a = alert;
export const a0 = a;
export const a1 = select(a, a => scaleLightness(0.23 * alertLightenShiftFactor, a));
export const a1_5 = select(a, a => scaleLightness(0.38 * alertLightenShiftFactor, a));
export const a2 = select(a, a => scaleLightness(0.54 * alertLightenShiftFactor, a));
export const w = warning;
export const w0 = w;
export const w1 = select(w, w => scaleLightness(0.3 * warningLightenShiftFactor, w));
export const w1_5 = select(w, w => scaleLightness(0.48 * warningLightenShiftFactor, w));
export const w2 = select(w, w => scaleLightness(0.67 * warningLightenShiftFactor, w));
export const s = success;
export const s0 = s;
export const s1 = select(s, s => scaleLightness(0.1 * successLightenShiftFactor, s));
export const s1_5 = select(s, s => scaleLightness(0.51 * successLightenShiftFactor, s));
export const s2 = select(s, s => scaleLightness(0.69 * successLightenShiftFactor, s));
export const i = informational;
export const i0 = i;
export const i1 = select(i, i => scaleLightness(0.22 * informationLightenShiftFactor, i));
export const i1_5 = select(i, i => scaleLightness(0.4 * informationLightenShiftFactor, i));
export const i2 = select(i, i => scaleLightness(0.58 * informationLightenShiftFactor, i));

// Special High-Contrast Colors
export const hcPrimaryHover = () => '#14344C';
export const hcStandardSelected = () => '#00FFFF';
export const hcStandardSelectedInactive = () => '#00A8A8';
export const hcStandardHover = () => '#004D4D';
export const hcDisabled = () => '#F16822';
export const hcPlaceholder = hcDisabled;
export const hcFocus = () => '#EDE833';
export const hcBusy = () => '#22A7F0';
const colors = {
  // New Tokens
  colorNeutral,
  colorStatusNeutral,
  colorPrimary,
  colorSecondary,
  colorAlert,
  colorWarning,
  colorSuccess,
  colorInfo,
  colorUiLight,
  colorUiMedium,
  colorUiDark,
  colorUiBorder,
  colorUiPop,
  colorTextDefault,
  colorTextLabel,
  colorTextInverse,
  colorTextPrimary,
  colorTextSecondary,
  colorTextPop,
  colorGraph1,
  colorGraph2,
  colorGraph3,
  colorGraph4,
  colorGraph5,
  colorGraph6,
  colorGraph7,
  colorGraph8,
  colorGraphAlt1,
  colorGraphAlt2,
  colorGraphAlt3,
  colorGraphAlt4,
  colorGraphAlt5,
  colorGraphAlt6,
  colorGraphAlt7,
  colorGraphAlt8,
  colorKPI1,
  colorKPI3,
  colorKPI5,
  // Old Tokens
  white,
  black,
  neutral,
  primary,
  secondary,
  alert,
  warning,
  success,
  informational,
  white80,
  white60,
  white30,
  white20,
  white15,
  white10,
  black60,
  n,
  n0,
  n5,
  n4,
  n3,
  n2,
  n1,
  n_1,
  n_2,
  n_3,
  n_4,
  n_5,
  p,
  p0,
  p5,
  p4,
  p3,
  p2,
  p1,
  p_1,
  p_2,
  p_3,
  e,
  e0,
  e1,
  e2,
  a,
  a0,
  a1,
  a1_5,
  a2,
  w,
  w0,
  w1,
  w1_5,
  w2,
  s,
  s0,
  s1,
  s1_5,
  s2,
  i,
  i0,
  i1,
  i1_5,
  i2,
  hcPrimaryHover,
  hcStandardSelected,
  hcStandardSelectedInactive,
  hcStandardHover,
  hcDisabled,
  hcPlaceholder,
  hcFocus,
  hcBusy
};
export default colors;
//# sourceMappingURL=colors.js.map